import {
  Radar,
  Wand2,
  LayoutDashboard,
  Cable,
  Bell,
  FileBarChart,
  Database,
  Code2,
  FileCode2,
  Cpu,
  Workflow,
  GitBranch,
  WifiOff,
} from 'lucide-react';

const features = [
  { icon: Cpu, title: 'Preconfigured Appliance', text: 'Ships with the software installed, plus built-in I/O, RS-232/RS-485 serial ports, and Wi-Fi.', color: '#3b82f6' },
  { icon: Radar, title: 'Network Scanner', text: 'Scan your subnets, fingerprint PLCs and CNCs, and adopt a device in one click.', color: '#06b6d4' },
  { icon: Wand2, title: 'Smart Assist', text: 'AI classifies a device\u2019s tags and builds a dashboard, even on an air-gapped appliance.', color: '#7c3aed' },
  { icon: LayoutDashboard, title: 'Dashboards', text: '40+ widget types bound to live tags, with layouts for desktop, tablet, and phone, plus kiosk and TV.', color: '#3b82f6' },
  { icon: Cable, title: '25+ Native Drivers', text: 'Modbus, OPC-UA, FOCAS, MTConnect, EtherNet/IP, Siemens S7, robots, and more.', color: '#06b6d4' },
  { icon: GitBranch, title: 'Ladder Logic & SFC', text: 'A soft-PLC ladder editor and sequential function charts, with live monitoring and bit forcing.', color: '#3b82f6' },
  { icon: Bell, title: 'Alarm Pipelines', text: 'Multi-stage escalation by email, SMS, and voice call to whoever is on call, with acknowledge and shelve.', color: '#dc2626' },
  { icon: FileBarChart, title: 'Reports & OEE', text: 'OEE and downtime Pareto by shift, plus PDF and CSV reports emailed on a schedule.', color: '#16a34a' },
  { icon: Database, title: 'Historian', text: 'Built-in time-series storage, switched on per tag, with deadbands and hourly and daily rollups.', color: '#ca8a04' },
  { icon: Workflow, title: 'Visual Workflows', text: 'Trigger on tag changes or schedules, run branches in parallel, write tags, call APIs, or notify.', color: '#06b6d4' },
  { icon: Code2, title: 'JS Scripting', text: 'Custom calculations and logic in a sandboxed JavaScript runtime.', color: '#ca8a04' },
  { icon: FileCode2, title: 'Custom Pages', text: 'Build any interface in HTML, CSS, and JavaScript with live tag bindings.', color: '#7c3aed' },
];

export function PlatformOverview() {
  return (
    <div className="pdf-page bg-white relative overflow-hidden flex flex-col">
      {/* Background blobs */}
      <div className="pdf-blob-decoration absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full blur-[100px] pointer-events-none" style={{ backgroundColor: '#3b82f6', opacity: 0.06 }} />
      <div className="pdf-blob-decoration absolute -bottom-20 -left-20 w-[200px] h-[200px] rounded-full blur-[80px] pointer-events-none" style={{ backgroundColor: '#7c3aed', opacity: 0.05 }} />
      <div className="pdf-blob-decoration absolute top-1/3 right-1/4 w-[250px] h-[250px] rounded-full blur-[100px] pointer-events-none" style={{ backgroundColor: '#06b6d4', opacity: 0.04 }} />

      {/* Header */}
      <header className="flex items-center justify-between px-10 pt-8 pb-4 relative z-10 shrink-0">
        <img src="/scada-ink-logo.png" alt="Scada.Ink" className="h-8" />
        <span className="text-xs font-medium px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary">
          Platform Overview
        </span>
      </header>

      <div className="mx-10 h-px bg-glass-border relative z-10 shrink-0" />

      {/* Content */}
      <div className="px-10 pt-6 relative z-10 flex-1 min-h-0 overflow-hidden">
        <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-text-primary">
          Monitor Your{' '}
          <span className="text-brand-primary">
            Factory Floor
          </span>
          , Not Your Cloud Bill
        </h1>
        <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-2xl">
          Scada.Ink is a complete industrial monitoring platform delivered as a preconfigured appliance.
          25+ native drivers, real-time dashboards, logic and workflow automation, alarms, and a built-in historian — all running locally with no internet connection required.
        </p>

        {/* Feature grid */}
        <div className="mt-6 grid grid-cols-3 gap-3">
          {features.map((f) => (
            <div key={f.title} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-glass-border bg-white/60">
              <div
                className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                style={{ backgroundColor: `${f.color}10`, color: f.color }}
              >
                <f.icon size={14} />
              </div>
              <div className="min-w-0">
                <h3 className="text-[10px] font-semibold text-text-primary leading-tight">{f.title}</h3>
                <p className="text-[8px] text-text-secondary leading-snug mt-0.5">{f.text}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom highlights */}
        <div className="mt-5 flex items-center justify-center gap-6">
          <div className="text-center">
            <div className="text-2xl font-extrabold text-brand-primary">25+</div>
            <div className="text-[9px] text-text-muted">Native Drivers</div>
          </div>
          <div className="w-px h-8 bg-glass-border" />
          <div className="text-center">
            <div className="text-2xl font-extrabold text-brand-primary">40+</div>
            <div className="text-[9px] text-text-muted">Widget Types</div>
          </div>
          <div className="w-px h-8 bg-glass-border" />
          <div className="text-center flex flex-col items-center">
            <WifiOff size={20} className="text-brand-primary mb-0.5" />
            <div className="text-[9px] text-text-muted">Zero Internet</div>
          </div>
          <div className="w-px h-8 bg-glass-border" />
          <div className="text-center flex flex-col items-center">
            <Cpu size={20} className="text-brand-secondary mb-0.5" />
            <div className="text-[9px] text-text-muted">Preconfigured</div>
          </div>
        </div>
      </div>

      {/* Factory floor illustration */}
      <div className="absolute bottom-10 left-0 right-0 pointer-events-none z-0" style={{ opacity: 0.035 }}>
        <FactoryFloorOverview />
      </div>

      {/* Footer */}
      <footer className="px-10 py-3 flex items-center justify-between border-t border-glass-border bg-white relative z-10 shrink-0">
        <span className="text-xs text-text-muted">
          Scada.Ink &mdash; Industrial Monitoring &amp; Analytics
        </span>
        <span className="text-[10px] text-text-muted">
          &copy; 2026 Scada.Ink. All rights reserved.
        </span>
        <span className="text-xs font-medium text-brand-primary">
          scada.ink
        </span>
      </footer>
    </div>
  );
}

function FactoryFloorOverview() {
  return (
    <svg viewBox="0 0 816 200" className="w-full" preserveAspectRatio="none">
      <line x1="0" y1="170" x2="816" y2="170" stroke="#1e293b" strokeWidth="2" />
      <rect x="30" y="90" width="80" height="80" rx="4" fill="#1e293b" />
      <rect x="35" y="95" width="30" height="20" rx="2" fill="#64748b" />
      <rect x="45" y="130" width="55" height="40" rx="2" fill="#334155" />
      <rect x="70" y="95" width="35" height="30" rx="2" fill="#475569" />
      <rect x="130" y="150" width="120" height="20" rx="4" fill="#1e293b" />
      <circle cx="140" cy="170" r="8" fill="#334155" />
      <circle cx="140" cy="170" r="4" fill="#64748b" />
      <circle cx="240" cy="170" r="8" fill="#334155" />
      <circle cx="240" cy="170" r="4" fill="#64748b" />
      <rect x="155" y="142" width="12" height="10" rx="1" fill="#475569" />
      <rect x="180" y="142" width="12" height="10" rx="1" fill="#475569" />
      <rect x="205" y="142" width="12" height="10" rx="1" fill="#475569" />
      <rect x="280" y="140" width="20" height="30" rx="2" fill="#1e293b" />
      <line x1="290" y1="140" x2="310" y2="100" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
      <line x1="310" y1="100" x2="340" y2="85" stroke="#1e293b" strokeWidth="5" strokeLinecap="round" />
      <line x1="340" y1="85" x2="355" y2="95" stroke="#1e293b" strokeWidth="4" strokeLinecap="round" />
      <circle cx="310" cy="100" r="5" fill="#475569" />
      <circle cx="340" cy="85" r="4" fill="#475569" />
      <rect x="380" y="60" width="70" height="110" rx="4" fill="#1e293b" />
      <rect x="385" y="65" width="60" height="35" rx="2" fill="#334155" />
      <rect x="395" y="110" width="40" height="50" rx="2" fill="#475569" />
      <circle cx="415" cy="82" r="10" fill="#475569" />
      <rect x="470" y="100" width="40" height="70" rx="3" fill="#1e293b" />
      <rect x="475" y="105" width="30" height="18" rx="2" fill="#3b82f6" opacity="0.3" />
      <rect x="530" y="80" width="90" height="90" rx="4" fill="#1e293b" />
      <rect x="535" y="85" width="40" height="25" rx="2" fill="#475569" />
      <rect x="540" y="120" width="70" height="45" rx="2" fill="#334155" />
      <rect x="640" y="70" width="50" height="100" fill="none" stroke="#1e293b" strokeWidth="3" />
      <line x1="640" y1="100" x2="690" y2="100" stroke="#1e293b" strokeWidth="2" />
      <line x1="640" y1="130" x2="690" y2="130" stroke="#1e293b" strokeWidth="2" />
      <rect x="720" y="140" width="40" height="25" rx="3" fill="#1e293b" />
      <rect x="715" y="120" width="8" height="50" rx="1" fill="#1e293b" />
      <circle cx="728" cy="170" r="7" fill="#334155" />
      <circle cx="752" cy="170" r="7" fill="#334155" />
      <rect x="0" y="20" width="816" height="4" fill="#1e293b" opacity="0.3" />
    </svg>
  );
}
